import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Clock, Calendar, FileText, Users, BarChart3, LogOut, Menu, X, User as UserIcon, CalendarDays, CreditCard, ClockPlus } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';

function Layout({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchPendingLeaveCount();
    }
  }, [user]);

  const fetchPendingLeaveCount = async () => {
    try {
      const response = await authFetch(`${API}/leave/pending-count`);
      if (response.ok) {
        const data = await response.json();
        setPendingLeaveCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching pending leave count:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, {
        method: 'POST'
      });
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      navigate('/login');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: BarChart3 },
    { name: 'Time Tracking', path: '/time-tracking', icon: Clock },
    { name: 'Attendance', path: '/attendance', icon: Calendar },
    { name: 'Leave', path: '/leave', icon: FileText },
    ...(user?.role === 'admin' ? [
      { name: 'Team', path: '/team', icon: Users },
      { name: 'Holidays', path: '/holidays', icon: CalendarDays },
      { name: 'Leave Credits', path: '/leave-credits', icon: CreditCard },
      { name: 'Manual Time Entry', path: '/admin/manual-time-entry', icon: ClockPlus }
    ] : []),
    { name: 'Reports', path: '/reports', icon: BarChart3 },
    { name: 'Profile', path: '/profile', icon: UserIcon }
  ];

  return (
    <div className="min-h-screen bg-background-alt">
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-primary rounded-full p-2">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-foreground-DEFAULT">TimeFlow</span>
          </div>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-40 transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <div className="bg-primary rounded-full p-2">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-foreground-DEFAULT">TimeFlow</span>
          </div>

          <nav className="space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all relative ${
                    isActive
                      ? 'bg-primary text-white shadow-lg shadow-primary/20'
                      : 'text-foreground-muted hover:bg-slate-50'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                  {item.name === 'Leave' && user?.role === 'admin' && pendingLeaveCount > 0 && (
                    <span className="absolute right-3 top-3 bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="leave-badge">
                      {pendingLeaveCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-10 h-10 rounded-full object-cover border border-slate-200"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            {!user?.picture && (
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-primary" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-foreground-DEFAULT truncate">{user?.name}</p>
              <p className="text-xs text-foreground-muted truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-all"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}

export default Layout;